import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';

interface ThemeInfo {
  id: string;
  label: string;
}

interface InterviewStatus {
  themes_completed: ThemeInfo[];
  themes_remaining: ThemeInfo[];
  current_theme: string | null;
  is_complete: boolean;
}

export default function InterviewPage() {
  const [status, setStatus] = useState<InterviewStatus | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [currentThemeLabel, setCurrentThemeLabel] = useState<string>('');
  const [answer, setAnswer] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'interviewer' | 'you'; text: string }>>([]);
  const [itemsAdded, setItemsAdded] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadStatus(); }, []);

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function loadStatus() {
    try {
      const data = await api.get<InterviewStatus>('/api/kb/interview/status');
      setStatus(data);
    } catch (err) {
      console.error('Interview status load failed:', err);
      // Show error to user for debugging
      setStatus({
        themes_completed: [],
        themes_remaining: [],
        current_theme: null,
        is_complete: false,
      });
    }
    setInitialLoading(false);
  }

  async function startTheme(themeId?: string) {
    setLoading(true);
    setMessages([]);
    setItemsAdded(0);
    try {
      const body = themeId ? { theme: themeId } : {};
      const data = await api.post<{ theme: string; theme_label: string; question: string; resumed?: boolean; conversation_history?: Array<{ role: string; content: string }> }>('/api/kb/interview/start', body);
      setCurrentThemeLabel(data.theme_label);

      if (data.resumed && data.conversation_history) {
        // Restore the full conversation from paused state
        const restored = data.conversation_history.map((m) => ({
          role: m.role === 'assistant' ? 'interviewer' as const : 'you' as const,
          text: m.content,
        }));
        setMessages(restored);
      } else {
        setMessages([{ role: 'interviewer', text: data.question }]);
      }
      setCurrentQuestion(data.question);
    } catch (err) {
      const msg = (err as Error).message || 'Unknown error';
      alert(`Start interview failed: ${msg}\n\nIf this keeps happening, check browser console (F12) for details.`);
    }
    setLoading(false);
  }

  async function submitAnswer() {
    if (!answer.trim() || loading) return;
    setLoading(true);

    const userMessage = answer.trim();
    setMessages((prev) => [...prev, { role: 'you', text: userMessage }]);
    setAnswer('');

    try {
      const data = await api.post<{
        reply: string;
        items_added: number;
        theme_complete: boolean;
        themes_remaining?: ThemeInfo[];
        is_interview_complete?: boolean;
      }>('/api/kb/interview/respond', { answer: userMessage });

      setMessages((prev) => [...prev, { role: 'interviewer', text: data.reply }]);
      setItemsAdded((prev) => prev + data.items_added);

      if (data.theme_complete) {
        setCurrentQuestion(null);
        loadStatus();
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'interviewer', text: 'Sorry, something went wrong. Try again.' }]);
    }
    setLoading(false);
  }

  async function skipTheme() {
    setLoading(true);
    try {
      await api.post('/api/kb/interview/skip', {});
      setCurrentQuestion(null);
      setMessages([]);
      loadStatus();
    } catch {}
    setLoading(false);
  }

  async function pauseTheme() {
    setLoading(true);
    try {
      await api.post('/api/kb/interview/pause', {});
      setCurrentQuestion(null);
      setMessages([]);
      loadStatus();
    } catch {}
    setLoading(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitAnswer();
    }
  }

  if (initialLoading) {
    return <p className="text-gray-500">Loading...</p>;
  }

  // Interview complete
  if (status?.is_complete && !currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Starter Interview</h1>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <p className="text-green-800 text-lg font-medium">🎉 All themes complete!</p>
          <p className="text-green-700 mt-2">Your knowledge base has been seeded with your professional profile. Head to the Knowledge Base page to review what was captured.</p>
        </div>
      </div>
    );
  }

  // Theme selection (no active theme)
  if (!currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Starter Interview</h1>
        <p className="text-gray-600 mb-6">
          A guided conversation to mine your hidden experience. We'll cover 5 themes — even small things count.
        </p>

        {status?.themes_completed && status.themes_completed.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-500 mb-2">Completed</h2>
            <div className="flex flex-wrap gap-2">
              {status.themes_completed.map((t) => (
                <span key={t.id} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  ✓ {t.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {status?.themes_remaining && status.themes_remaining.length > 0 && (
          <div>
            <h2 className="text-sm font-medium text-gray-500 mb-2">Remaining themes</h2>
            <div className="space-y-2">
              {status.themes_remaining.map((t: { id: string; label: string; paused?: boolean }) => (
                <button
                  key={t.id}
                  onClick={() => startTheme(t.id)}
                  disabled={loading}
                  className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all disabled:opacity-50"
                >
                  <span className="font-medium text-gray-900">{t.label}</span>
                  {t.paused && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                      paused
                    </span>
                  )}
                  <span className="ml-2 text-gray-400">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {(!status?.themes_remaining || status.themes_remaining.length === 0) && !status?.is_complete && (
          <button
            onClick={() => startTheme()}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Starting...' : 'Start Interview'}
          </button>
        )}
      </div>
    );
  }

  // Active conversation
  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-10rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Starter Interview</h1>
          <p className="text-sm text-gray-500">Theme: {currentThemeLabel}</p>
        </div>
        <div className="flex items-center gap-3">
          {itemsAdded > 0 && (
            <span className="text-sm text-green-600 font-medium">
              +{itemsAdded} item{itemsAdded > 1 ? 's' : ''} added to KB
            </span>
          )}
          <button
            onClick={pauseTheme}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-200 rounded hover:border-blue-400"
          >
            Pause topic
          </button>
          <button
            onClick={skipTheme}
            disabled={loading}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 border border-gray-200 rounded hover:border-gray-400"
          >
            Skip theme
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-2" role="log" aria-label="Interview conversation">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'you' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                msg.role === 'you'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </span>
              <p className="text-sm text-gray-500">Working on it...</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your answer... (Enter to send, Shift+Enter for new line)"
          className="flex-1 p-3 border border-gray-300 rounded-lg text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Your answer"
          disabled={loading}
        />
        <button
          onClick={submitAnswer}
          disabled={loading || !answer.trim()}
          className="bg-blue-600 text-white px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 self-end h-16"
          aria-label="Send answer"
        >
          Send
        </button>
      </div>
    </div>
  );
}
